import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';
import { mapErrorToUserMessage } from '../utils/userErrorMessage';
import analyticsService from '../services/analyticsService';
import { BrandLogo, FaeBillMark } from '../components/common/BrandLogo';
import { setPendingInviteToken } from '../utils/advisorInviteToken';
import { setClientInviteToken } from '../utils/clientInviteToken';
import { clearAdvisorLandingSeen } from '../utils/advisorLandingSession';

const LoginPage: React.FC = () => {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // Um ggf. Weiterleitungsziel zu holen
  const { currentUser } = useAuth();

  // Plan-ID aus URL-Query-Parameter auslesen (?plan=...)
  const planId = new URLSearchParams(location.search).get('plan');

  // Redirect-Ziel nach Login (von ProtectedRoute übergeben, oder /)
  const from = location.state?.from?.pathname || "/dashboard";

  // M11b: Capture the ?invite= token into localStorage so it survives the
  // auth redirect chain (login → verify-email → dashboard).
  React.useEffect(() => {
    const inviteToken = new URLSearchParams(location.search).get('invite');
    if (inviteToken) {
      setPendingInviteToken(inviteToken);
    }
  }, [location.search]);

  // WL-K5a: Capture the ?clientInvite= token (Kanzlei→Mandant reverse invite) into
  // its own localStorage key (separate from the advisor token, B-E5).
  React.useEffect(() => {
    const clientInviteToken = new URLSearchParams(location.search).get('clientInvite');
    if (clientInviteToken) {
      setClientInviteToken(clientInviteToken);
    }
  }, [location.search]);

  React.useEffect(() => {
    if (currentUser) {
        // Wenn schon eingeloggt und verifiziert, weiterleiten
        if (currentUser.emailVerified) {
             // Leite zum ursprünglichen Ziel oder Dashboard weiter
             // Füge planId zum State hinzu, falls vorhanden
             navigate(from, { state: { ...(location.state || {}), plan: planId }, replace: true });
        } else {
             // Wenn eingeloggt aber nicht verifiziert, zur Verifizierungsseite
             // Füge planId zum State hinzu
             navigate('/verify-email', { state: { email: currentUser.email, plan: planId }, replace: true });
        }
    }
  }, [currentUser, navigate, from, planId]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Analytics: GA4-Standard `login` nach erfolgreichem Credential-Login —
      // VOR dem emailVerified-Branch, feuert also für verifizierte UND
      // unverifizierte Logins (Auto-Session-Restore in AuthContext feuert bewusst
      // NICHT). Direkter Service-Aufruf (kein Hook), damit die Login-Seite keine
      // zusätzlichen Page-View-/Engagement-Effekte erbt.
      analyticsService.trackLogin({ method: 'email' });
      // M-ACF-3 (`architecture-advisor-cockpit-first.md` §3.1 Punkt 4 / §7.6):
      // Jeder echte Credential-Login ist ein frischer Session-Einstieg — den
      // Session-Marker löschen, VOR beiden `navigate`-Zweigen, damit der
      // Advisor-Landing-Redirect wieder feuert (auch Logout→Re-Login desselben
      // Nutzers im selben Tab, E6). Bewusst NICHT im Auto-Redirect-Effekt oben
      // (der feuert auch bei Session-Restore = kein echter Login). Flag off ⇒
      // Helper ist no-op.
      clearAdvisorLandingSeen();
      // NEU: Prüfung auf Verifizierung nach Login
      if (userCredential.user.emailVerified) {
          // Leite zum ursprünglichen Ziel oder Dashboard weiter
          // Füge planId zum State hinzu, falls vorhanden
          navigate(from, { state: { ...(location.state || {}), plan: planId }, replace: true });
      } else {
          // E-Mail nicht verifiziert, leite zur Verifizierungsseite
          // Füge planId zum State hinzu
          navigate('/verify-email', { state: { email: userCredential.user.email, plan: planId }, replace: true });
      }
    } catch (err: any) {
      logger.error(err, "login error");
      // `err.code` (auth/invalid-credential|wrong-password|user-not-found) wird
      // bereits von Stufe 1 (FIREBASE_CODE_MAP) auf errors.auth.invalidCredentials
      // gemappt — der bisherige generische else-Zweig (unbekannter Auth-Fehler,
      // z.B. auth/user-disabled) landet ohne fallbackKey korrekt bei Stufe 5
      // (errors.generic), NICHT bei invalidCredentials (irreführend bei einem
      // Nicht-Passwort-Fehler).
      setError(mapErrorToUserMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center auth-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-4 left-4">
        <Link to="/" className="flex items-center">
          <BrandLogo className="h-10" />
        </Link>
      </div>

      <div className="bg-surface-raised p-8 rounded-lg shadow-xl w-full max-w-md border border-hairline">
        <div className="flex justify-center mb-6">
          <FaeBillMark className="h-12 w-12" />
        </div>
        <h2 className="text-2xl font-bold mb-6 text-center text-primary">Anmelden</h2>
        <form onSubmit={handleLogin}>
          {error && (
            <div className="mb-4 bg-[var(--tone-danger-bg)] border-l-4 border-[var(--tone-danger-border)] p-4">
              <p className="text-primary text-sm">{error}</p>
            </div>
          )}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-secondary mb-1">E-Mail</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-strong bg-surface-inset text-primary rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="ihre@email.de"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-secondary mb-1">Passwort</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border border-strong bg-surface-inset text-primary rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="********"
            />
          </div>
          <div className="mb-6 text-right">
            <Link to="/forgot-password" className="text-sm text-accent-ink hover:text-accent-ink transition-colors">
              Passwort vergessen?
            </Link>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 text-[#0C1322] py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Anmeldung läuft...' : 'Anmelden'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-secondary">
          Noch kein Konto?{' '}
          <Link to="/register" className="font-medium text-accent-ink hover:text-accent-ink transition-colors">
            Jetzt registrieren
          </Link>
        </p>
        <p className="mt-2 text-center text-sm">
          <a href={process.env.REACT_APP_MARKETING_DOMAIN || 'https://faebill.com'} className="text-muted hover:text-secondary transition-colors">
            Zurück zur Startseite
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage; 